import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Users, 
  Calculator, 
  FileText, 
  Settings, 
  LogOut, 
  Sun,
  ChevronLeft,
  ChevronRight,
  PiggyBank,
  Receipt,
  UserPlus,
  Calendar,
  Route,
  BarChart3,
  Shield,
  PenTool,
  Euro,
  Package,
  Wrench,
  FileCheck,
  Warehouse
} from "lucide-react";

const Sidebar = ({ user, logout }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);

  // Build navigation array
  const baseNavigation = [
    // Dashboard pour tous sauf Telepro
    { name: "Dashboard", href: "/", icon: LayoutDashboard, roles: ["admin", "commercial", "regie", "administratif"] },
    
    // Prospects - pour les Telepro et Admin (vision globale)
    { name: "Prospects", href: "/prospects", icon: UserPlus, roles: ["telepro", "admin", "confirmateur", "superviseur"] },
    
    // Planning - pour Telepro, Commercial, Régie, Admin, Administratif
    { name: "Planning", href: "/planning", icon: Calendar, roles: ["telepro", "commercial", "regie", "admin", "administratif", "confirmateur", "superviseur"] },
    
    // Distribution RDV - uniquement pour Admin et Administratif
    { name: "Distribution RDV", href: "/distribution-rdv", icon: Route, roles: ["admin", "administratif", "confirmateur"] },
    
    // Statistiques - uniquement pour Admin
    { name: "Statistiques", href: "/statistiques", icon: BarChart3, roles: ["admin"] },
    
    // Clients
    { name: "Clients", href: "/clients", icon: Users, roles: ["admin", "commercial", "regie", "administratif"] },
    
    // NOUVEAU: Projets (remplace Contrats) - Accessible aux Telepro pour suivre leurs clients
    { name: "Projets", href: "/projets", icon: Sun, roles: ["admin", "commercial", "administratif", "telepro", "confirmateur", "superviseur"] },
    
    // Simulateur
    { name: "Simulateur", href: "/simulator", icon: Calculator, roles: ["admin", "commercial"] },
    
    // NOUVEAU: Produits & Catalogue
    { name: "Produits", href: "/produits", icon: Package, roles: ["admin", "administratif"] },
    
    // NOUVEAU: Dossiers Aides (CEE, MaPrimeRénov)
    { name: "Dossiers Aides", href: "/dossiers-aides", icon: FileCheck, roles: ["admin", "administratif"] },
    
    // NOUVEAU: Stock / Inventaire - PAS pour Régie
    { name: "Stock", href: "/stock", icon: Warehouse, roles: ["admin", "administratif"] },
    
    // Devis & Factures
    { name: "Devis & Factures", href: "/devis-factures", icon: Receipt, roles: ["admin", "administratif"] },
    
    // Trésorerie
    { name: "Trésorerie", href: "/treasury", icon: PiggyBank, roles: ["admin"] },
    
    // Mes Commissions
    { name: "Mes Commissions", href: "/my-commissions", icon: Euro, roles: ["commercial", "regie", "telepro", "confirmateur", "superviseur"] },
    
    // NOUVEAU: Gestion Équipe (pour Régie uniquement)
    { name: "Gestion Équipe", href: "/team-management", icon: Users, roles: ["regie"] },
    
    // NOUVEAU: SAV - PAS pour Régie
    { name: "SAV", href: "/sav", icon: Wrench, roles: ["admin", "administratif"] },
    
    // Signature BDC
    { name: "Signature BDC", href: "/signature-bdc", icon: PenTool, roles: ["commercial", "admin", "administratif"] },
    
    // Paramètres
    { name: "Paramètres", href: "/settings", icon: Settings, roles: ["admin"] },
  ];

  // Add Audit Logs for super admin only
  const navigation = user?.is_super_admin
    ? [...baseNavigation, { name: "Audit & Logs", href: "/audit-logs", icon: Shield, roles: ["admin"] }]
    : baseNavigation;

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: "bg-red-100 text-red-800",
      commercial: "bg-blue-100 text-blue-800", 
      regie: "bg-green-100 text-green-800",
      administratif: "bg-purple-100 text-purple-800",
      telepro: "bg-orange-100 text-orange-800",
      confirmateur: "bg-teal-100 text-teal-800",
      superviseur: "bg-indigo-100 text-indigo-800"
    };
    return colors[role] || "bg-gray-100 text-gray-800";
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrateur",
      commercial: "Commercial",
      regie: "Régie", 
      administratif: "Administratif",
      telepro: "Telepro",
      confirmateur: "Confirmateur",
      superviseur: "Superviseur"
    };
    return labels[role] || role;
  };

  return (
    <div className={`${collapsed ? 'w-16' : 'w-64'} bg-white border-r border-gray-200 flex flex-col transition-all duration-300 shadow-sm`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {!collapsed && (
            <div className="flex flex-col items-center w-full space-y-1">
              <img 
                src="/logo-sunlife.jpg"
                alt="SUNLIFE"
                className="h-16 w-auto object-contain"
              />
              <p className="text-slate-600 font-medium text-[10px] tracking-wide">CRM DE GESTION</p>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="p-2 hover:bg-gray-100 absolute right-2"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* User Info */}
      {!collapsed && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center text-white font-medium">
              {user.first_name?.[0]}{user.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user.email}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(user.role)} mt-1`}>
                {getRoleLabel(user.role)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navigation
          .filter(item => item.roles.includes(user.role))
          .map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-red-50 text-red-600 font-medium shadow-sm'
                    : 'text-slate-600 hover:bg-gray-50 hover:text-slate-900'
                }`}
                title={collapsed ? item.name : ''}
              >
                <item.icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-red-600' : ''}`} />
                {!collapsed && (
                  <span className="flex-1 text-sm">{item.name}</span>
                )}
              </Link>
            );
          })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <Button
          onClick={logout}
          variant="ghost"
          className={`w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50 ${
            collapsed ? 'px-2' : 'px-3'
          }`}
          title={collapsed ? 'Déconnexion' : ''}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="ml-3 text-sm">Déconnexion</span>}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;